import { createContext, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router';
import { useLocalStorage } from 'beautiful-react-hooks';
import { jwtDecode } from 'jwt-decode';

import STORAGE from 'src/constants/localStorage';

import useTokenRefresh from 'src/hooks/useTokenRefresh';

import api from 'src/api';
import { DEFAULT_CURRENCY } from 'src/constants/countryCurrency';
import useReleaseNotes from 'src/hooks/useReleaseNotes';

// Set only while a forced logout (expired session) is navigating to /login, so the unsaved-changes
// guard and the leak-record Back-trap stand down instead of prompting on a session that can't be saved.
export const forcedLogout = { active: false };

function buildUserInfo(authUser) {
  const company = authUser.company;
  return {
    name: authUser.name,
    email: authUser.email,
    uid: authUser.id,
    companyId: company?.id ?? null,
    companyName: company?.name ?? null,
    photoURL: authUser.photoUrl || null,
    role: authUser.roles?.[0] ?? null,
    companySlug: company?.slug ?? null,
    companyCurrency: company?.currency || DEFAULT_CURRENCY,
  };
}

const AuthContext = createContext({
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  handleLogin: () => Promise.resolve(),
  handleLogout: () => {},
  handlePasswordResetRequest: () => Promise.resolve(),
  handlePasswordReset: () => Promise.resolve(),
});

export function AuthProvider({ children }) {
  const [user, setUser] = useLocalStorage(STORAGE.AUTH_USER, null);
  const [accExpiresAt, setAccExpiresAt] = useLocalStorage(STORAGE.TOKEN.ACCESS_EXPIRES_AT, null);
  const [refExpiresAt, setRefExpiresAt] = useLocalStorage(STORAGE.TOKEN.REFRESH_EXPIRES_AT, null);
  const [, setHighlightedRow] = useLocalStorage(STORAGE.LEAK_RECORDS.HIGHLIGHTED, null);
  const [, setSortColumn] = useLocalStorage(STORAGE.LEAK_RECORDS.SORT_COLUMN, null);
  const { latest } = useReleaseNotes();

  const history = useHistory();

  const access = useMemo(() => {
    const accessToken = localStorage.getItem(STORAGE.TOKEN.ACCESS);
    return typeof accessToken === 'string' && accessToken !== 'null' ? jwtDecode(accessToken) : null;
  }, [user, accExpiresAt]);

  const isAuthenticated = useMemo(() => {
    return typeof user !== 'undefined' && user !== null;
  }, [user]);

  function handleLogin(email, password, serial) {
    return api.auth
      .login(email, password, serial)
      .then(([authUser, { accessTokenExpiresAt, refreshTokenExpiresAt }]) => {
        // save token expiration data in local storage
        setAccExpiresAt(accessTokenExpiresAt);
        setRefExpiresAt(refreshTokenExpiresAt);

        const userInfo = buildUserInfo(authUser);
        setUser(userInfo);

        // Decode and return access token so callers don't need to re-decode
        const accessToken = localStorage.getItem(STORAGE.TOKEN.ACCESS);
        const decodedAccess = accessToken ? jwtDecode(accessToken) : null;

        return { user: userInfo, access: decodedAccess };
      })
      .catch((err) => {
        setUser(undefined);
        throw err;
      });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- stable reference for useTokenRefresh timer;
  // closure-captured deps (history, setUser, etc.) are stable hook returns.
  const handleLogout = useCallback((forced) => {
    api.auth.logout();
    // A forced logout (expired session) tears the record page down mid-edit; flag the whole
    // teardown so the unsaved-changes guard AND the record's Back-trap stand down (session can't save).
    if (forced === true) forcedLogout.active = true;
    try {
      // clean up token expiration
      setAccExpiresAt(null);
      setRefExpiresAt(null);
      setUser(null);
      setHighlightedRow(null);
      setSortColumn(null);
      localStorage.removeItem(STORAGE.COMPANIES.SELECTED);
      localStorage.removeItem(`${STORAGE.LEAK_RECORDS.COLUMNS}.${latest?.version || 'none'}`);
      // clean up KPI dashboard settings
      localStorage.removeItem(STORAGE.DASHBOARD.TIMEFRAME);
      localStorage.removeItem(STORAGE.DASHBOARD.TIMEFRAME_PRESET);
      localStorage.removeItem(STORAGE.DASHBOARD.DATA_SCOPE);
      // TODO: Remove after v5.4 release — legacy cleanup from KPI dashboard migration
      localStorage.removeItem('dashboard__datasets');
      localStorage.removeItem('dashboard__filters');
      localStorage.removeItem('dashboard__charts');

      if (!history.location.pathname.endsWith('login')) {
        // Don't redirect back to /account after login (logout button is there)
        const shouldRedirectBack = !history.location.pathname.includes('/account');
        history.push('/login', shouldRedirectBack ? { from: history.location.pathname } : undefined);
      }
    } finally {
      forcedLogout.active = false;
    }
  }, []);

  function handleProfileUpdate(userData) {
    setUser({
      ...user,
      ...userData,
    });
  }

  function handlePasswordResetRequest(email) {
    return api.users.passwordResetRequest(email);
  }

  function handlePasswordReset(actionCode, newPassword) {
    return api.users.passwordResetCode(actionCode, newPassword);
  }

  function handleIntegration(key) {
    return api.auth
      .integration(key)
      .then(([authUser, { accessTokenExpiresAt, refreshTokenExpiresAt }]) => {
        const userInfo = buildUserInfo(authUser);
        setUser(userInfo);
        setAccExpiresAt(accessTokenExpiresAt);
        setRefExpiresAt(refreshTokenExpiresAt);

        const accessToken = localStorage.getItem(STORAGE.TOKEN.ACCESS);
        const decodedAccess = accessToken ? jwtDecode(accessToken) : null;
        return { user: userInfo, access: decodedAccess };
      })
      .catch((err) => {
        setUser(undefined);
        throw err;
      });
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps -- all captured deps are stable:
  // handleLogout (useCallback with []), setAccExpiresAt/setRefExpiresAt (stable hook setters).
  const handleRefresh = useCallback(() => {
    api.auth
      .loginWithRefreshToken()
      .then(({ accessTokenExpiresAt, refreshTokenExpiresAt }) => {
        // save token expiration data in local storage
        setAccExpiresAt(accessTokenExpiresAt);
        setRefExpiresAt(refreshTokenExpiresAt);
      })
      .catch(() => {
        // in case of any error during token refresh clean up everything and redirect user
        handleLogout(true);
      });
  }, []);

  const handleExpire = useCallback(() => handleLogout(true), [handleLogout]);

  useTokenRefresh({
    accExpiresAt,
    refExpiresAt,
    onRefresh: handleRefresh,
    onExpire: handleExpire,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps -- handler functions intentionally omitted;
  // they capture current `user` via closure and `user` is already in the dep array.
  // Full useCallback wrapping is a larger refactor tracked separately.
  const contextValue = useMemo(
    () => ({
      isAuthenticated,
      user,
      access,
      isFeatureActive: (feature) => api.features.getFeatureViewable(feature),
      handleLogin,
      handleLogout,
      handleProfileUpdate,
      handlePasswordResetRequest,
      handlePasswordReset,
      handleIntegration,
    }),
    [isAuthenticated, user, access]
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
};

export default AuthContext;
